export type AuthStatusLike = 'configuring' | 'authenticated' | 'unauthenticated' | string;
export type PersonaProvisioningStatusLike =
  | 'idle'
  | 'in_progress'
  | 'complete'
  | 'failed'
  | string;

export type AuthRouteRenderState =
  | 'logout_redirect'
  | 'configuring'
  | 'failure'
  | 'persona_selection'
  | 'persona_provisioning'
  | 'token_refresh'
  | 'assistant_bridge'
  | 'redirect'
  | 'public_auth';

export const resolveAuthRouteRenderState = ({
  authStatus,
  logoutTarget,
  personaProvisioningStatus,
  effectivePersona,
  personaTokenReady,
  isBridgeCallback,
  postAuthPersonaSelection = false,
  personaSelectionAllowed = true,
}: {
  authStatus: AuthStatusLike;
  logoutTarget: string | null;
  personaProvisioningStatus: PersonaProvisioningStatusLike;
  effectivePersona: string;
  personaTokenReady: boolean;
  isBridgeCallback: boolean;
  postAuthPersonaSelection?: boolean;
  personaSelectionAllowed?: boolean;
}): AuthRouteRenderState => {
  if (authStatus === 'configuring') {
    return logoutTarget ? 'logout_redirect' : 'configuring';
  }

  if (authStatus === 'authenticated') {
    if (logoutTarget) {
      return 'logout_redirect';
    }
    if (personaProvisioningStatus === 'failed') {
      return 'failure';
    }
    const selectionRequested =
      personaProvisioningStatus === 'selection_required' || postAuthPersonaSelection;
    if (selectionRequested && personaSelectionAllowed) {
      return 'persona_selection';
    }
    if (selectionRequested && effectivePersona === 'guest') {
      return 'failure';
    }
    if (personaProvisioningStatus === 'in_progress') {
      return 'persona_provisioning';
    }
    if (
      personaProvisioningStatus === 'idle'
      && effectivePersona === 'guest'
      && !personaTokenReady
    ) {
      return 'configuring';
    }
    if (effectivePersona !== 'guest' && !personaTokenReady) {
      return 'token_refresh';
    }
    return isBridgeCallback ? 'assistant_bridge' : 'redirect';
  }

  return 'public_auth';
};

export type AuthGateRenderState =
  | 'logout_redirect'
  | 'configuring'
  | 'redirect_to_auth'
  | 'failure'
  | 'persona_selection'
  | 'persona_provisioning'
  | 'token_refresh'
  | 'children';

export const resolveAuthGateRenderState = ({
  authStatus,
  logoutTarget,
  personaProvisioningStatus,
  effectivePersona,
  personaTokenReady,
  postAuthPersonaSelection = false,
  personaSelectionAllowed = true,
}: {
  authStatus: AuthStatusLike;
  logoutTarget: string | null;
  personaProvisioningStatus: PersonaProvisioningStatusLike;
  effectivePersona: string;
  personaTokenReady: boolean;
  postAuthPersonaSelection?: boolean;
  personaSelectionAllowed?: boolean;
}): AuthGateRenderState => {
  if (authStatus === 'configuring') {
    return logoutTarget ? 'logout_redirect' : 'configuring';
  }

  if (authStatus !== 'authenticated') {
    return logoutTarget ? 'logout_redirect' : 'redirect_to_auth';
  }

  if (personaProvisioningStatus === 'failed') {
    return logoutTarget ? 'logout_redirect' : 'failure';
  }

  const selectionRequested =
    personaProvisioningStatus === 'selection_required' || postAuthPersonaSelection;
  if (selectionRequested && personaSelectionAllowed) {
    return logoutTarget ? 'logout_redirect' : 'persona_selection';
  }
  if (selectionRequested && effectivePersona === 'guest') {
    return logoutTarget ? 'logout_redirect' : 'failure';
  }

  if (personaProvisioningStatus === 'in_progress') {
    return logoutTarget ? 'logout_redirect' : 'persona_provisioning';
  }

  if (
    personaProvisioningStatus === 'idle'
    && effectivePersona === 'guest'
    && !personaTokenReady
  ) {
    return logoutTarget ? 'logout_redirect' : 'configuring';
  }

  if (effectivePersona !== 'guest' && !personaTokenReady) {
    return logoutTarget ? 'logout_redirect' : 'token_refresh';
  }

  return 'children';
};

export type RootEntryRenderState =
  | 'failure'
  | 'persona_selection'
  | 'persona_provisioning'
  | 'token_refresh'
  | 'redirect'
  | 'logout_redirect'
  | 'configuring'
  | 'apply';

export const resolveRootEntryRenderState = ({
  authStatus,
  logoutTarget,
  personaProvisioningStatus,
  effectivePersona,
  personaTokenReady,
  postAuthPersonaSelection = false,
  personaSelectionAllowed = true,
}: {
  authStatus: AuthStatusLike;
  logoutTarget: string | null;
  personaProvisioningStatus: PersonaProvisioningStatusLike;
  effectivePersona: string;
  personaTokenReady: boolean;
  postAuthPersonaSelection?: boolean;
  personaSelectionAllowed?: boolean;
}): RootEntryRenderState => {
  if (authStatus === 'authenticated') {
    if (personaProvisioningStatus === 'failed') {
      return 'failure';
    }
    const selectionRequested =
      personaProvisioningStatus === 'selection_required' || postAuthPersonaSelection;
    if (selectionRequested && personaSelectionAllowed) {
      return 'persona_selection';
    }
    if (selectionRequested && effectivePersona === 'guest') {
      return 'failure';
    }
    if (personaProvisioningStatus === 'in_progress') {
      return 'persona_provisioning';
    }
    if (
      personaProvisioningStatus === 'idle'
      && effectivePersona === 'guest'
      && !personaTokenReady
    ) {
      return 'configuring';
    }
    if (effectivePersona !== 'guest' && !personaTokenReady) {
      return 'token_refresh';
    }
    return 'redirect';
  }

  if (authStatus === 'configuring') {
    return logoutTarget ? 'logout_redirect' : 'configuring';
  }

  return 'apply';
};
